import { useState } from 'react';
import { FolderSearch, X, Loader2, File, Image as ImageIcon, Video, FileText, CheckCircle, AlertCircle } from 'lucide-react';

interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  thumbnailLink?: string;
  webContentLink?: string;
  createdTime: string;
}

interface DriveSelectorProps {
  cuid: string;
  fileType: string;
  label: string;
  onSelectSuccess: (url: string) => void;
}

export default function DriveSelector({ cuid, fileType, label, onSelectSuccess }: DriveSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<DriveFile[]>([]);
  const [error, setError] = useState('');
  const [selectingId, setSelectingId] = useState<string | null>(null);

  const API_URL = import.meta.env.VITE_API_URL || 'https://heritage-os-api.uplaytv3117.workers.dev/api/v1';

  const fetchFiles = async () => {
    setLoading(true);
    setError('');
    const token = localStorage.getItem('jwt_token');
    
    try {
      const res = await fetch(`${API_URL}/stories/${cuid}/media`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch files');
      
      setFiles(data.files || []);
    } catch (err: any) {
      setError(err.message || 'Error fetching files');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    fetchFiles();
  };

  const handleSelect = async (fileId: string) => {
    setSelectingId(fileId);
    setError('');
    
    const token = localStorage.getItem('jwt_token');
    
    try {
      const res = await fetch(`${API_URL}/stories/${cuid}/media/${fileType}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ file_id: fileId })
      });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to assign file');
      
      // Append version string to bust cache
      onSelectSuccess(`${API_URL}/stories/${cuid}/media/${fileType}?v=${Date.now()}`);
      setIsOpen(false);
      
    } catch (err: any) {
      setError(err.message || 'Error assigning file');
    } finally {
      setSelectingId(null);
    }
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return <ImageIcon className="w-8 h-8 text-blue-500" />;
    if (mimeType.startsWith('video/')) return <Video className="w-8 h-8 text-purple-500" />;
    if (mimeType.startsWith('audio/')) return <FileText className="w-8 h-8 text-green-500" />;
    return <File className="w-8 h-8 text-slate-500" />;
  };

  return (
    <>
      <button 
        type="button"
        onClick={handleOpen}
        className="bg-slate-100 hover:bg-slate-200 px-3 py-1 border border-slate-300 rounded text-xs font-medium text-slate-700 flex items-center gap-1 transition-colors"
        title="Select from Drive"
      >
        <FolderSearch className="w-3 h-3 text-indigo-600" />
        <span>{label || 'Select'}</span>
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[80vh]">
            <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50">
              <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <FolderSearch className="w-5 h-5 text-indigo-600" />
                Select Existing File
              </h2>
              <button type="button" onClick={() => setIsOpen(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              {error && (
                <div className="mb-4 p-3 bg-red-50 text-red-700 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                  <p className="text-sm">{error}</p>
                </div>
              )}
              
              {loading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-indigo-600 animate-spin mb-4" />
                  <p className="text-slate-500 text-sm font-medium">Loading files from Drive...</p>
                </div>
              ) : files.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50">
                  <FolderSearch className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 font-medium">No files found in this story's folder</p>
                  <p className="text-sm text-slate-400 mt-1">Upload a file first to see it here</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {files.map((file) => (
                    <div 
                      key={file.id} 
                      className="border border-slate-200 rounded-lg overflow-hidden hover:border-indigo-300 hover:shadow-md transition-all group relative bg-white"
                    >
                      <div className="aspect-square bg-slate-100 flex items-center justify-center p-2 relative">
                        {file.thumbnailLink ? (
                          <img src={file.thumbnailLink} alt={file.name} className="w-full h-full object-cover rounded" />
                        ) : (
                          getFileIcon(file.mimeType)
                        )}
                        
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                          <button
                            type="button"
                            onClick={() => handleSelect(file.id)}
                            disabled={selectingId === file.id}
                            className="bg-indigo-600 text-white px-3 py-1.5 rounded-md text-sm font-medium shadow-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1"
                          >
                            {selectingId === file.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                            Select
                          </button>
                        </div>
                      </div>
                      <div className="p-2 border-t border-slate-100">
                        <p className="text-xs font-medium text-slate-700 truncate" title={file.name}>
                          {file.name}
                        </p>
                        <p className="text-[10px] text-slate-400 mt-0.5">
                          {new Date(file.createdTime).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end">
              <button 
                type="button"
                onClick={() => setIsOpen(false)}
                className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
